import { useTranslation } from 'react-i18next';
import SEO from '../../components/base/SEO';

export default function HybridWorkforcePage() {
  const { t } = useTranslation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://braincommerce.com';

  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Hybrid Workforce Architecture",
    "description": "A unified workforce architecture that integrates human expertise, AI intelligence, and robotic automation into a single operational framework.",
    "author": {
      "@type": "Organization",
      "name": "BrainCommerce"
    },
    "publisher": {
      "@type": "Organization",
      "name": "BrainCommerce"
    },
    "datePublished": "2025-01-15",
    "dateModified": "2025-01-15"
  };

  return (
    <>
      <SEO
        title="Hybrid Workforce Architecture - Humans, AI & Robotics | BrainCommerce"
        description="A unified workforce architecture that integrates human expertise, AI intelligence, and robotic automation. Learn how each component works together to redesign work."
        keywords="hybrid workforce, workforce architecture, human AI collaboration, robotic automation, workforce integration, future of work"
        canonical={`${siteUrl}/hybrid-workforce`}
        schema={schema}
      />
      
      <div className="min-h-screen bg-white">
        {/* Definition Hero */}
        <section className="pt-24 md:pt-32 lg:pt-48 pb-16 md:pb-32 px-4 md:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-[96px] font-extralight text-[#1a1a1a] mb-6 md:mb-8">
              {t('hybrid.hero.title')}
            </h1>
            <p className="text-lg md:text-xl lg:text-2xl text-[#4a4a4a] max-w-[840px] mx-auto leading-relaxed">
              {t('hybrid.hero.definition')}
            </p>
          </div>

          {/* Icon Diagrams */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16 mt-20 md:mt-40">
            <div className="text-center">
              <div className="w-24 h-24 md:w-32 md:h-32 rounded-full border-2 border-[#1a1a1a] flex items-center justify-center mb-4 md:mb-6">
                <i className="ri-user-line text-3xl md:text-5xl text-[#1a1a1a]"></i>
              </div>
              <span className="text-xs md:text-sm uppercase tracking-wider text-[#4a4a4a]">{t('hybrid.hero.human')}</span>
            </div>

            <div className="w-0.5 h-12 md:w-24 md:h-0.5 bg-[#1a1a1a]"></div>

            <div className="text-center">
              <div className="w-24 h-24 md:w-32 md:h-32 rounded-full border-2 border-[#1a1a1a] flex items-center justify-center mb-4 md:mb-6">
                <i className="ri-brain-line text-3xl md:text-5xl text-[#1a1a1a]"></i>
              </div>
              <span className="text-xs md:text-sm uppercase tracking-wider text-[#4a4a4a]">{t('hybrid.hero.ai')}</span>
            </div>

            <div className="w-0.5 h-12 md:w-24 md:h-0.5 bg-[#1a1a1a]"></div>

            <div className="text-center">
              <div className="w-24 h-24 md:w-32 md:h-32 rounded-full border-2 border-[#1a1a1a] flex items-center justify-center mb-4 md:mb-6">
                <i className="ri-robot-line text-3xl md:text-5xl text-[#1a1a1a]"></i>
              </div>
              <span className="text-xs md:text-sm uppercase tracking-wider text-[#4a4a4a]">{t('hybrid.hero.robotics')}</span>
            </div>
          </div>
        </section>

        {/* Explanation Grid */}
        <section className="py-12 md:py-24 px-4 md:px-8 lg:px-32">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Human Card */}
            <div className="bg-white rounded-lg p-6 md:p-12 shadow-[0_4px_24px_rgba(0,0,0,0.06)] min-h-[500px] md:h-[720px] flex flex-col">
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-[#1a1a1a] flex items-center justify-center mb-6 md:mb-8">
                <i className="ri-user-line text-2xl md:text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-light text-[#1a1a1a] mb-4 md:mb-6">{t('hybrid.roles.human.title')}</h3>
              <div className="flex-grow space-y-3 md:space-y-4 mb-6 md:mb-8">
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-lg md:text-xl text-[#1a1a1a] mt-1"></i>
                  <p className="text-sm md:text-base text-[#4a4a4a]">{t('hybrid.roles.human.point1')}</p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-lg md:text-xl text-[#1a1a1a] mt-1"></i>
                  <p className="text-sm md:text-base text-[#4a4a4a]">{t('hybrid.roles.human.point2')}</p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-lg md:text-xl text-[#1a1a1a] mt-1"></i>
                  <p className="text-sm md:text-base text-[#4a4a4a]">{t('hybrid.roles.human.point3')}</p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-lg md:text-xl text-[#1a1a1a] mt-1"></i>
                  <p className="text-sm md:text-base text-[#4a4a4a]">{t('hybrid.roles.human.point4')}</p>
                </div>
              </div>
              <a href="#" className="text-xs md:text-sm text-[#1a1a1a] underline cursor-pointer hover:no-underline">{t('hybrid.roles.learnMore')}</a>
            </div>

            {/* AI Card - Elevated */}
            <div className="bg-gradient-to-b from-white to-[#e8f2f7] rounded-lg p-6 md:p-12 shadow-[0_4px_24px_rgba(0,0,0,0.06)] min-h-[500px] md:h-[720px] flex flex-col md:-mt-10">
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-[#1a1a1a] flex items-center justify-center mb-6 md:mb-8">
                <i className="ri-brain-line text-2xl md:text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-light text-[#1a1a1a] mb-4 md:mb-6">{t('hybrid.roles.ai.title')}</h3>
              <div className="flex-grow space-y-3 md:space-y-4 mb-6 md:mb-8">
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-lg md:text-xl text-[#1a1a1a] mt-1"></i>
                  <p className="text-sm md:text-base text-[#4a4a4a]">{t('hybrid.roles.ai.point1')}</p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-lg md:text-xl text-[#1a1a1a] mt-1"></i>
                  <p className="text-sm md:text-base text-[#4a4a4a]">{t('hybrid.roles.ai.point2')}</p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-lg md:text-xl text-[#1a1a1a] mt-1"></i>
                  <p className="text-sm md:text-base text-[#4a4a4a]">{t('hybrid.roles.ai.point3')}</p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-lg md:text-xl text-[#1a1a1a] mt-1"></i>
                  <p className="text-sm md:text-base text-[#4a4a4a]">{t('hybrid.roles.ai.point4')}</p>
                </div>
              </div>
              <a href="#" className="text-xs md:text-sm text-[#1a1a1a] underline cursor-pointer hover:no-underline">{t('hybrid.roles.learnMore')}</a>
            </div>

            {/* Robotics Card */}
            <div className="bg-white rounded-lg p-6 md:p-12 shadow-[0_4px_24px_rgba(0,0,0,0.06)] min-h-[500px] md:h-[720px] flex flex-col">
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-[#1a1a1a] flex items-center justify-center mb-6 md:mb-8">
                <i className="ri-robot-line text-2xl md:text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-light text-[#1a1a1a] mb-4 md:mb-6">{t('hybrid.roles.robotics.title')}</h3>
              <div className="flex-grow space-y-3 md:space-y-4 mb-6 md:mb-8">
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-lg md:text-xl text-[#1a1a1a] mt-1"></i>
                  <p className="text-sm md:text-base text-[#4a4a4a]">{t('hybrid.roles.robotics.point1')}</p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-lg md:text-xl text-[#1a1a1a] mt-1"></i>
                  <p className="text-sm md:text-base text-[#4a4a4a]">{t('hybrid.roles.robotics.point2')}</p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-lg md:text-xl text-[#1a1a1a] mt-1"></i>
                  <p className="text-sm md:text-base text-[#4a4a4a]">{t('hybrid.roles.robotics.point3')}</p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-lg md:text-xl text-[#1a1a1a] mt-1"></i>
                  <p className="text-sm md:text-base text-[#4a4a4a]">{t('hybrid.roles.robotics.point4')}</p>
                </div>
              </div>
              <a href="#" className="text-xs md:text-sm text-[#1a1a1a] underline cursor-pointer hover:no-underline">{t('hybrid.roles.learnMore')}</a>
            </div>
          </div>
        </section>

        {/* Diagram Section */}
        <section className="bg-[#f8f8f8] py-16 md:py-32 mt-16 md:mt-32">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-light text-center text-[#1a1a1a] mb-12 md:mb-20">{t('hybrid.diagram.title')}</h2>
            
            <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12 overflow-x-auto pb-4">
              <div className="text-center flex-shrink-0">
                <div className="w-32 h-32 md:w-40 md:h-40 rounded-lg border-2 border-[#1a1a1a] flex items-center justify-center mb-2 md:mb-4">
                  <span className="text-base md:text-lg font-light">{t('hybrid.diagram.task')}</span>
                </div>
              </div>

              <i className="ri-arrow-right-line md:ri-arrow-right-line text-2xl md:text-4xl text-[#1a1a1a] rotate-90 md:rotate-0"></i>

              <div className="text-center flex-shrink-0">
                <div className="w-32 h-32 md:w-40 md:h-40 rounded-lg bg-[#1a1a1a] flex items-center justify-center mb-2 md:mb-4">
                  <span className="text-base md:text-lg font-light text-white">{t('hybrid.diagram.human')}</span>
                </div>
                <span className="text-xs md:text-sm text-[#4a4a4a]">{t('hybrid.diagram.humanRole')}</span>
              </div>

              <i className="ri-arrow-right-line text-2xl md:text-4xl text-[#1a1a1a] rotate-90 md:rotate-0"></i>

              <div className="text-center flex-shrink-0">
                <div className="w-32 h-32 md:w-40 md:h-40 rounded-lg bg-[#e8f2f7] border-2 border-[#1a1a1a] flex items-center justify-center mb-2 md:mb-4">
                  <span className="text-base md:text-lg font-light">{t('hybrid.diagram.ai')}</span>
                </div>
                <span className="text-xs md:text-sm text-[#4a4a4a]">{t('hybrid.diagram.aiRole')}</span>
              </div>

              <i className="ri-arrow-right-line text-2xl md:text-4xl text-[#1a1a1a] rotate-90 md:rotate-0"></i>

              <div className="text-center flex-shrink-0">
                <div className="w-32 h-32 md:w-40 md:h-40 rounded-lg bg-[#1a1a1a] flex items-center justify-center mb-2 md:mb-4">
                  <span className="text-base md:text-lg font-light text-white">{t('hybrid.diagram.robotics')}</span>
                </div>
                <span className="text-xs md:text-sm text-[#4a4a4a]">{t('hybrid.diagram.roboticsRole')}</span>
              </div>

              <i className="ri-arrow-right-line text-2xl md:text-4xl text-[#1a1a1a] rotate-90 md:rotate-0"></i>

              <div className="text-center flex-shrink-0">
                <div className="w-32 h-32 md:w-40 md:h-40 rounded-lg border-2 border-[#1a1a1a] flex items-center justify-center mb-2 md:mb-4">
                  <span className="text-base md:text-lg font-light">{t('hybrid.diagram.result')}</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
